import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useEmployee } from '../contexts/EmployeeContext';
import { Globe as Globe2, Building2, Languages, ArrowLeft, Users, MapPin, Clock, Code2, Calendar, TrendingUp, Compass, Tag } from 'lucide-react';
import { useHQ } from '../contexts/HQContext';
import { haversineKm, formatDistanceToHQ, computeTeamDistanceStats, formatDistance, tzLabel, TZ_ABBR } from '../utils/geoUtils';
import { normalizeCountry } from '../utils/countryUtils';
import TeamIcon from '../components/TeamIcon';
import { fetchAllTeams } from '../utils/api';
import type { Team } from '../types/employee';

// ─── helpers ────────────────────────────────────────────────────────────────

function countBy<T>(items: T[], key: (item: T) => string): [string, number][] {
  const map = new Map<string, number>();
  for (const item of items) {
    const k = key(item);
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

function countByMulti<T>(items: T[], key: (item: T) => string[]): [string, number][] {
  const map = new Map<string, number>();
  for (const item of items) {
    for (const k of key(item)) {
      if (k) map.set(k, (map.get(k) ?? 0) + 1);
    }
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

function tenureLabel(startDate: string): string {
  const months = Math.floor(
    (Date.now() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
  );
  if (months < 6) return '< 6 months';
  if (months < 12) return '6–12 months';
  if (months < 24) return '1–2 years';
  if (months < 36) return '2–3 years';
  if (months < 60) return '3–5 years';
  return '5+ years';
}

// (tzLabel is imported from geoUtils)



function parseTzOffset(label: string): number {
  const m = label.match(/UTC([+\u2212-])(\d+(?::\d+)?)/);
  if (!m) return 0;
  const sign = m[1] === '+' ? 1 : -1;
  const [h, min = '0'] = m[2].split(':');
  return sign * (parseInt(h) + parseInt(min) / 60);
}

// ─── sub-components ─────────────────────────────────────────────────────────

const SectionHeader = ({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) => (
  <div className="flex items-start gap-3 mb-5">
    <div className="p-2 bg-primary-600/15 rounded-lg text-primary-400 mt-0.5">{icon}</div>
    <div>
      <h2 className="text-base font-semibold text-bolt-text">{title}</h2>
      <p className="text-xs text-bolt-textSubtle mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const HBar = ({ label, count, max, color = 'bg-primary-600' }: {
  label: string; count: number; max: number; color?: string;
}) => (
  <div className="flex items-center gap-3 group">
    <div className="w-32 shrink-0 text-xs text-bolt-textMuted text-right truncate">{label}</div>
    <div className="flex-1 h-6 bg-bolt-bg rounded-md overflow-hidden">
      <div
        className={`h-full ${color} rounded-md transition-all duration-500 ease-out flex items-center justify-end pr-2`}
        style={{ width: `${Math.max((count / max) * 100, 4)}%` }}
      >
        <span className="text-[10px] font-semibold text-white/90">{count}</span>
      </div>
    </div>
  </div>
);

const DonutSlice = ({ percent, offset, color }: { percent: number; offset: number; color: string }) => {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = (percent / 100) * circ;
  const gap = circ - dash;
  return (
    <circle
      cx="50" cy="50" r={r}
      fill="none"
      stroke={color}
      strokeWidth="14"
      strokeDasharray={`${dash} ${gap}`}
      strokeDashoffset={-offset * circ / 100}
      style={{ transition: 'stroke-dasharray 0.6s ease' }}
    />
  );
};

const DONUT_COLORS = [
  '#0175C2', '#34D399', '#FB923C', '#60A5FA', '#FBBF24',
  '#F87171', '#A78BFA', '#2DD4BF', '#E879F9', '#94A3B8',
];

function Donut({ data }: { data: [string, number][] }) {
  const total = data.reduce((s, [, n]) => s + n, 0);
  let offset = 0;
  const slices = data.map(([label, count], i) => {
    const pct = (count / total) * 100;
    const slice = { label, count, pct, offset, color: DONUT_COLORS[i % DONUT_COLORS.length] };
    offset += pct;
    return slice;
  });

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="w-28 h-28 shrink-0 -rotate-90">
        <circle cx="50" cy="50" r="40" fill="none" stroke="var(--bolt-surface-hover)" strokeWidth="14" />
        {slices.map((s, i) => (
          <DonutSlice key={i} percent={s.pct} offset={s.offset} color={s.color} />
        ))}
      </svg>
      <div className="flex flex-col gap-1.5 min-w-0">
        {slices.map((s, i) => (
          <div key={i} className="flex items-center gap-2 min-w-0">
            <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-xs text-bolt-textMuted truncate">{s.label}</span>
            <span className="text-xs font-semibold text-bolt-text ml-auto pl-2">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const StatPill = ({ label, value, color = 'text-primary-400' }: { label: string; value: string | number; color?: string }) => (
  <div className="bg-bolt-bg border border-bolt-border rounded-lg px-4 py-3 flex flex-col gap-0.5">
    <span className={`text-xl font-bold ${color}`}>{value}</span>
    <span className="text-xs text-bolt-textSubtle">{label}</span>
  </div>
);

// ─── page ────────────────────────────────────────────────────────────────────

const InsightsPage = () => {
  const { employees, isLoading } = useEmployee();
  const navigate = useNavigate();
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const { hq } = useHQ();

  useEffect(() => {
    document.title = 'Team Insights – StackBlitz Team';
  }, []);

  useEffect(() => {
    fetchAllTeams().then(setAllTeams).catch(() => {});
  }, []);

  const data = useMemo(() => {
    const byDept = countBy(employees, e => e.department);
    const byCountry = countBy(employees, e => normalizeCountry(e.location.country));
    const byCity = countBy(employees, e => e.location.city);
    const byTz = countBy(employees, e => tzLabel(e.location.timezone));
    const byLang = countByMulti(employees, e => e.languages);
    const bySkill = countByMulti(employees, e => e.skills);
    const byTenure = countBy(employees, e => tenureLabel(e.startDate));
    const byEmployer = countBy(employees, e => e.employer);

    const tenureOrder = ['< 6 months', '6–12 months', '1–2 years', '2–3 years', '3–5 years', '5+ years'];
    const byTenureSorted = tenureOrder
      .map(label => byTenure.find(([l]) => l === label) ?? [label, 0] as [string, number])
      .filter(([, n]) => n > 0);

    // Build team stats: map from teamId → { team, count }
    const teamCountMap = new Map<string, { team: Team; count: number }>();
    for (const emp of employees) {
      for (const team of emp.teams ?? []) {
        const entry = teamCountMap.get(team.id) ?? { team, count: 0 };
        entry.count += 1;
        teamCountMap.set(team.id, entry);
      }
    }
    const byTeam: { team: Team; count: number }[] = [...teamCountMap.values()].sort((a, b) => b.count - a.count);

    const distanceStats = computeTeamDistanceStats(employees);

    // Build label for a whole-hour offset (no employees — used for gap filling)
    const offsetToLabel = (h: number): string => {
      const sign = h >= 0 ? '+' : '\u2212';
      const display = String(Math.abs(h));
      const abbr = TZ_ABBR[h];
      return abbr ? `UTC${sign}${display} (${abbr})` : `UTC${sign}${display}`;
    };

    // Sort occupied timezones west→east
    const byTzSorted = [...byTz].sort((a, b) => parseTzOffset(a[0]) - parseTzOffset(b[0]));

    // Fill in zero-count columns for every whole-hour offset between min and max
    const minOffset = byTzSorted.length ? parseTzOffset(byTzSorted[0][0]) : 0;
    const maxOffset = byTzSorted.length ? parseTzOffset(byTzSorted[byTzSorted.length - 1][0]) : 0;
    const occupiedOffsets = new Set(byTzSorted.map(([l]) => parseTzOffset(l)));
    const byTzFilled: [string, number][] = [];
    for (let h = Math.ceil(minOffset); h <= Math.floor(maxOffset); h++) {
      if (occupiedOffsets.has(h)) {
        byTzFilled.push(byTzSorted.find(([l]) => parseTzOffset(l) === h)!);
      } else {
        byTzFilled.push([offsetToLabel(h), 0]);
      }
    }

    return { byDept, byCountry, byCity, byTz: byTzFilled, byLang, bySkill, byTenureSorted, byEmployer, byTeam, distanceStats };
  }, [employees]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-sm text-bolt-textMuted">Loading insights...</p>
      </div>
    );
  }

  const uniqueCountries = new Set(employees.map(e => normalizeCountry(e.location.country)).filter(Boolean)).size;
  const uniqueTimezones = new Set(employees.map(e => tzLabel(e.location.timezone))).size;
  const uniqueDepts = new Set(employees.map(e => e.department)).size;
  const uniqueLangs = new Set(employees.flatMap(e => e.languages)).size;
  const maxBar = (arr: [string, number][]) => Math.max(...arr.map(([, n]) => n), 1);

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="p-1.5 rounded-md text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <p className="section-header mb-1">Team Insights</p>
          <h1 className="text-2xl font-bold text-bolt-text">StackBlitz Team Overview</h1>
        </div>
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatPill value={employees.length} label="Total members" color="text-primary-400" />
        <StatPill value={uniqueCountries} label="Countries" color="text-success-400" />
        <StatPill value={uniqueTimezones} label="Time zones" color="text-accent-400" />
        <StatPill value={uniqueLangs} label="Languages spoken" color="text-warning-400" />
      </div>

      {/* Row 1: Departments + Locations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Departments */}
        <div className="card p-5">
          <SectionHeader
            icon={<Building2 className="h-4 w-4" />}
            title="Departments"
            subtitle={`${uniqueDepts} departments across the organization`}
          />
          <div className="space-y-2.5">
            {data.byDept.map(([dept, count]) => (
              <HBar key={dept} label={dept} count={count} max={maxBar(data.byDept)} />
            ))}
          </div>
        </div>

        {/* Locations */}
        <div className="card p-5">
          <SectionHeader
            icon={<MapPin className="h-4 w-4" />}
            title="Locations"
            subtitle={`Team spread across ${uniqueCountries} countries`}
          />
          <div className="mb-4">
            <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">By Country</p>
            <div className="space-y-2">
              {data.byCountry.map(([country, count]) => (
                <HBar key={country} label={country} count={count} max={maxBar(data.byCountry)} color="bg-success-500" />
              ))}
            </div>
          </div>
          {data.byCity.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">Top Cities</p>
              <div className="space-y-2">
                {data.byCity.slice(0, 5).map(([city, count]) => (
                  <HBar key={city} label={city.split(',')[0]} count={count} max={maxBar(data.byCity)} color="bg-success-600/70" />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Time Zones + Languages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Time Zones */}
        <div className="card p-5 flex flex-col">
          <SectionHeader
            icon={<Clock className="h-4 w-4" />}
            title="Time Zones"
            subtitle={`${data.byTz.filter(([,c]) => c > 0).length} time zone groups spanning the globe`}
          />
          {(() => {
            const tzMax = maxBar(data.byTz);
            const total = data.byTz.length;

            // Determine which columns are currently in working hours (08:00–17:00 local)
            const now = new Date();
            const utcHour = now.getUTCHours() + now.getUTCMinutes() / 60;
            const inWorkingHours = (offset: number) => {
              const local = ((utcHour + offset) % 24 + 24) % 24;
              return local >= 8 && local < 17;
            };

            // Find contiguous ranges of in-hours columns (handles date-line wrap as two ranges)
            type Range = { start: number; end: number };
            const workRanges: Range[] = [];
            let rangeStart: number | null = null;
            data.byTz.forEach(([tz], i) => {
              const active = inWorkingHours(parseTzOffset(tz));
              if (active && rangeStart === null) rangeStart = i;
              if (!active && rangeStart !== null) { workRanges.push({ start: rangeStart, end: i - 1 }); rangeStart = null; }
            });
            if (rangeStart !== null) workRanges.push({ start: rangeStart, end: total - 1 });

            return (
              <div className="mt-4 flex-1 flex flex-col">
                {/* bar drawing area — grows to fill remaining card height */}
                <div className="relative flex items-end gap-1.5 flex-1">
                  {/* working-hours highlight bands */}
                  {workRanges.map((range, ri) => (
                    <div
                      key={ri}
                      className="absolute inset-y-0 pointer-events-none rounded-sm"
                      style={{
                        left: `calc(${(range.start / total) * 100}% + ${range.start > 0 ? '3px' : '0px'})`,
                        right: `calc(${((total - 1 - range.end) / total) * 100}% + ${range.end < total - 1 ? '3px' : '0px'})`,
                        background: 'rgba(234,130,30,0.08)',
                        borderLeft: '1px solid rgba(234,130,30,0.25)',
                        borderRight: '1px solid rgba(234,130,30,0.25)',
                      }}
                    />
                  ))}
                  {data.byTz.map(([tz, count]) => {
                    const isEmpty = count === 0;
                    const barPct = isEmpty ? 1.5 : Math.max((count / tzMax) * 100, 3);
                    return (
                      <div key={tz} className="flex-1 relative group min-w-0 flex items-end h-full">
                        <div
                          className={`w-full rounded-t-sm transition-all duration-300 ${isEmpty ? 'bg-bolt-border opacity-40' : 'bg-accent-500 group-hover:bg-accent-400'}`}
                          style={{ height: `${barPct}%` }}
                        />
                        {/* count label — omit for zero-count bars */}
                        {!isEmpty && (
                          <span
                            className="absolute left-0 right-0 text-center text-[9px] font-semibold text-white/90 pointer-events-none"
                            style={{ bottom: barPct > 12 ? 2 : `calc(${barPct}% + 2px)` }}
                          >
                            {count}
                          </span>
                        )}
                        {/* tooltip */}
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-bolt-surface border border-bolt-border rounded px-2 py-1 text-xs text-bolt-text whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10 shadow-lg transition-opacity">
                          <span className="font-semibold">{tz}</span>
                          <span className="ml-1.5 text-bolt-textSubtle">{isEmpty ? 'no members' : `${count} ${count === 1 ? 'member' : 'members'}`}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* UTC offset labels below bars */}
                <div className="flex gap-1.5 mt-1">
                  {data.byTz.map(([tz]) => (
                    <div key={tz} className="flex-1 min-w-0">
                      <span className="block text-[9px] text-bolt-textMuted text-center leading-tight truncate">
                        {tz.replace(/\s*\(.*\)/, '')}
                      </span>
                    </div>
                  ))}
                </div>
                {workRanges.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: 'rgba(234,130,30,0.18)', border: '1px solid rgba(234,130,30,0.35)' }} />
                    <span className="text-[10px] text-bolt-textSubtle">Working hours (08:00–17:00 local)</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Languages */}
        <div className="card p-5">
          <SectionHeader
            icon={<Languages className="h-4 w-4" />}
            title="Languages"
            subtitle={`${uniqueLangs} languages spoken by team members`}
          />
          {data.byLang.length <= 8 ? (
            <Donut data={data.byLang.slice(0, 8)} />
          ) : (
            <div className="space-y-2.5">
              {data.byLang.slice(0, 8).map(([lang, count]) => (
                <HBar key={lang} label={lang} count={count} max={maxBar(data.byLang)} color="bg-warning-500" />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Tenure + Skills + Employer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Tenure */}
        <div className="card p-5">
          <SectionHeader
            icon={<Calendar className="h-4 w-4" />}
            title="Tenure"
            subtitle="Length of service distribution"
          />
          <div className="space-y-2.5">
            {data.byTenureSorted.map(([label, count]) => (
              <HBar key={label} label={label} count={count} max={maxBar(data.byTenureSorted)} color="bg-secondary-500" />
            ))}
          </div>
        </div>

        {/* Top Skills */}
        <div className="card p-5">
          <SectionHeader
            icon={<Code2 className="h-4 w-4" />}
            title="Top Skills"
            subtitle="Most common skills on the team"
          />
          <div className="space-y-2.5">
            {data.bySkill.slice(0, 8).map(([skill, count]) => (
              <HBar key={skill} label={skill} count={count} max={maxBar(data.bySkill)} color="bg-primary-500" />
            ))}
          </div>
        </div>

        {/* Employer breakdown */}
        {data.byEmployer.length > 1 && (
          <div className="card p-5">
            <SectionHeader
              icon={<Users className="h-4 w-4" />}
              title="Employment Type"
              subtitle="Employees vs. contractors"
            />
            <Donut data={data.byEmployer} />
          </div>
        )}

        {data.byEmployer.length <= 1 && (
          <div className="card p-5">
            <SectionHeader
              icon={<TrendingUp className="h-4 w-4" />}
              title="Global Reach"
              subtitle="Geographic diversity score"
            />
            <div className="space-y-4 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-bolt-textMuted">Countries represented</span>
                <span className="text-sm font-semibold text-bolt-text">{uniqueCountries}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-bolt-textMuted">Continents</span>
                <span className="text-sm font-semibold text-bolt-text">
                  {new Set(employees.map(e => {
                    const c = e.location.country;
                    if (['United States', 'Canada', 'Mexico'].includes(c)) return 'Americas';
                    if (['Germany', 'France', 'United Kingdom', 'Netherlands', 'Spain', 'Italy', 'Sweden', 'Norway', 'Finland', 'Denmark', 'Poland', 'Ukraine'].includes(c)) return 'Europe';
                    if (['China', 'Japan', 'India', 'South Korea', 'Singapore', 'Indonesia', 'Australia'].includes(c)) return 'Asia-Pacific';
                    if (['Brazil', 'Argentina', 'Colombia', 'Chile'].includes(c)) return 'South America';
                    if (['Nigeria', 'South Africa', 'Kenya', 'Egypt', 'Morocco'].includes(c)) return 'Africa';
                    return 'Other';
                  })).size}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-bolt-textMuted">Members per country (avg)</span>
                <span className="text-sm font-semibold text-bolt-text">
                  {(employees.length / uniqueCountries).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-bolt-textMuted">Largest single location</span>
                <span className="text-sm font-semibold text-bolt-text">
                  {data.byCity[0]?.[0]?.split(',')[0] ?? '–'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Row 4: Teams */}
      {data.byTeam.length > 0 && (
        <div className="card p-5">
          <SectionHeader
            icon={<Tag className="h-4 w-4" />}
            title="Teams"
            subtitle={`${data.byTeam.length} active team${data.byTeam.length !== 1 ? 's' : ''} across the organisation`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {data.byTeam.map(({ team, count }) => {
              const max = data.byTeam[0]?.count ?? 1;
              return (
                <div key={team.id} className="flex items-center gap-3 group">
                  <div className="w-32 shrink-0 flex items-center justify-end gap-1.5 min-w-0">
                    <TeamIcon team={team} size={13} className="shrink-0 text-bolt-textSubtle" />
                    <span className="text-xs text-bolt-textMuted truncate">{team.name}</span>
                  </div>
                  <div className="flex-1 h-6 bg-bolt-bg rounded-md overflow-hidden">
                    <div
                      className="h-full bg-success-600 rounded-md transition-all duration-500 ease-out flex items-center justify-end pr-2"
                      style={{ width: `${Math.max((count / max) * 100, 4)}%` }}
                    >
                      <span className="text-[10px] font-semibold text-white/90">{count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Row 4b: Coke vs Pepsi */}
      {(() => {
        const cokeTeam = allTeams.find(t => t.name === 'Team Coke');
        const pepsiTeam = allTeams.find(t => t.name === 'Team Pepsi');
        if (!cokeTeam && !pepsiTeam) return null;
        const coke = data.byTeam.find(({ team }) => team.name === 'Team Coke');
        const pepsi = data.byTeam.find(({ team }) => team.name === 'Team Pepsi');
        const cokeCount = coke?.count ?? 0;
        const pepsiCount = pepsi?.count ?? 0;
        const total = cokeCount + pepsiCount;
        const cokePercent = total === 0 ? 50 : Math.round((cokeCount / total) * 100);
        const pepsiPercent = 100 - cokePercent;
        const leader = cokeCount > pepsiCount ? 'Coke' : pepsiCount > cokeCount ? 'Pepsi' : null;

        return (
          <div className="card p-5 overflow-hidden relative">
            {/* Decorative split background */}
            <div className="absolute inset-0 pointer-events-none">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-700 ease-out opacity-[0.04]"
                style={{ width: `${cokePercent}%`, background: '#E02020' }}
              />
              <div
                className="absolute inset-y-0 right-0 transition-all duration-700 ease-out opacity-[0.04]"
                style={{ width: `${pepsiPercent}%`, background: '#1A4AFF' }}
              />
            </div>

            <div className="relative">
              <div className="flex items-start gap-3 mb-6">
                <div className="p-2 bg-bolt-bg border border-bolt-border rounded-lg text-bolt-textSubtle mt-0.5">
                  <span className="text-sm">🥤</span>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-bolt-text">Coke vs Pepsi</h2>
                  <p className="text-xs text-bolt-textSubtle mt-0.5">
                    {total === 0
                      ? 'No one has picked a side yet — the great debate awaits'
                      : leader
                        ? `Team ${leader} is winning the cola wars`
                        : `It's a dead heat — the cola wars rage on`}
                  </p>
                </div>
                {leader && (
                  <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full border"
                    style={
                      leader === 'Coke'
                        ? { background: 'rgba(224,32,32,0.1)', color: '#F87171', borderColor: 'rgba(224,32,32,0.25)' }
                        : { background: 'rgba(26,74,255,0.1)', color: '#93C5FD', borderColor: 'rgba(26,74,255,0.25)' }
                    }
                  >
                    {leader} leads
                  </span>
                )}
              </div>

              {/* Side-by-side counts */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                {/* Coke */}
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl border transition-all"
                  style={{ borderColor: 'rgba(224,32,32,0.2)', background: 'rgba(224,32,32,0.05)' }}
                >
                  {cokeTeam ? (
                    <TeamIcon team={cokeTeam} size={28} className="text-red-400" />
                  ) : (
                    <span className="text-2xl">🥤</span>
                  )}
                  <span className="text-3xl font-bold" style={{ color: '#F87171' }}>{cokeCount}</span>
                  <span className="text-xs text-bolt-textMuted font-medium">Team Coke</span>
                  <span className="text-[11px] text-bolt-textSubtle">
                    {total === 0 ? '–' : `${cokePercent}% of cola fans`}
                  </span>
                </div>

                {/* Pepsi */}
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl border transition-all"
                  style={{ borderColor: 'rgba(26,74,255,0.2)', background: 'rgba(26,74,255,0.05)' }}
                >
                  {pepsiTeam ? (
                    <TeamIcon team={pepsiTeam} size={28} className="text-blue-400" />
                  ) : (
                    <span className="text-2xl">🫙</span>
                  )}
                  <span className="text-3xl font-bold" style={{ color: '#93C5FD' }}>{pepsiCount}</span>
                  <span className="text-xs text-bolt-textMuted font-medium">Team Pepsi</span>
                  <span className="text-[11px] text-bolt-textSubtle">
                    {total === 0 ? '–' : `${pepsiPercent}% of cola fans`}
                  </span>
                </div>
              </div>

              {/* Battle bar */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] font-semibold uppercase tracking-wide">
                  <span style={{ color: '#F87171' }}>Coke {total > 0 ? `${cokePercent}%` : ''}</span>
                  <span style={{ color: '#93C5FD' }}>{total > 0 ? `${pepsiPercent}%` : ''} Pepsi</span>
                </div>
                <div className="h-3 rounded-full overflow-hidden bg-bolt-bg flex">
                  <div
                    className="h-full rounded-l-full transition-all duration-700 ease-out"
                    style={{
                      width: `${cokePercent}%`,
                      background: 'linear-gradient(to right, #991b1b, #E02020)',
                    }}
                  />
                  <div
                    className="h-full rounded-r-full transition-all duration-700 ease-out"
                    style={{
                      width: `${pepsiPercent}%`,
                      background: 'linear-gradient(to left, #1e3a8a, #1A4AFF)',
                    }}
                  />
                </div>
                {total === 0 && (
                  <p className="text-[11px] text-bolt-textSubtle text-center pt-1 italic">
                    Join a team from your profile to cast your vote
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Row 4c: Cats vs Dogs */}
      {(() => {
        const allPets = employees.flatMap(e => e.pets ?? []);
        const catCount = allPets.filter(p => p.species === 'cat').length;
        const dogCount = allPets.filter(p => p.species === 'dog').length;
        const total = catCount + dogCount;
        if (total === 0) return null;
        const catPercent = Math.round((catCount / total) * 100);
        const dogPercent = 100 - catPercent;
        const leader = catCount > dogCount ? 'cats' : dogCount > catCount ? 'dogs' : null;

        return (
          <div className="card p-5 overflow-hidden relative">
            <div className="absolute inset-0 pointer-events-none">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-700 ease-out opacity-[0.04]"
                style={{ width: `${catPercent}%`, background: '#f59e0b' }}
              />
              <div
                className="absolute inset-y-0 right-0 transition-all duration-700 ease-out opacity-[0.04]"
                style={{ width: `${dogPercent}%`, background: '#0ea5e9' }}
              />
            </div>

            <div className="relative">
              <div className="flex items-start gap-3 mb-6">
                <div className="p-2 bg-bolt-bg border border-bolt-border rounded-lg text-bolt-textSubtle mt-0.5">
                  <span className="text-sm">🐾</span>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-bolt-text">Cats vs Dogs</h2>
                  <p className="text-xs text-bolt-textSubtle mt-0.5">
                    {leader
                      ? `Team ${leader} has the edge in the eternal pet debate`
                      : `It's a perfect tie — the great pet debate continues`}
                  </p>
                </div>
                {leader && (
                  <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full border"
                    style={
                      leader === 'cats'
                        ? { background: 'rgba(245,158,11,0.1)', color: '#fbbf24', borderColor: 'rgba(245,158,11,0.25)' }
                        : { background: 'rgba(14,165,233,0.1)', color: '#38bdf8', borderColor: 'rgba(14,165,233,0.25)' }
                    }
                  >
                    {leader.charAt(0).toUpperCase() + leader.slice(1)} lead
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl border transition-all"
                  style={{ borderColor: 'rgba(245,158,11,0.2)', background: 'rgba(245,158,11,0.05)' }}
                >
                  <span className="text-3xl leading-none">🐱</span>
                  <span className="text-3xl font-bold" style={{ color: '#fbbf24' }}>{catCount}</span>
                  <span className="text-xs text-bolt-textMuted font-medium">Cats</span>
                  <span className="text-[11px] text-bolt-textSubtle">{catPercent}% of cat & dog owners</span>
                </div>

                <div className="flex flex-col items-center gap-2 p-4 rounded-xl border transition-all"
                  style={{ borderColor: 'rgba(14,165,233,0.2)', background: 'rgba(14,165,233,0.05)' }}
                >
                  <span className="text-3xl leading-none">🐶</span>
                  <span className="text-3xl font-bold" style={{ color: '#38bdf8' }}>{dogCount}</span>
                  <span className="text-xs text-bolt-textMuted font-medium">Dogs</span>
                  <span className="text-[11px] text-bolt-textSubtle">{dogPercent}% of cat & dog owners</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] font-semibold uppercase tracking-wide">
                  <span style={{ color: '#fbbf24' }}>Cats {catPercent}%</span>
                  <span style={{ color: '#38bdf8' }}>{dogPercent}% Dogs</span>
                </div>
                <div className="h-3 rounded-full overflow-hidden bg-bolt-bg flex">
                  <div
                    className="h-full rounded-l-full transition-all duration-700 ease-out"
                    style={{ width: `${catPercent}%`, background: 'linear-gradient(to right, #b45309, #f59e0b)' }}
                  />
                  <div
                    className="h-full rounded-r-full transition-all duration-700 ease-out"
                    style={{ width: `${dogPercent}%`, background: 'linear-gradient(to left, #0369a1, #0ea5e9)' }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* HQ Distance Section */}
      {hq && (() => {
        const withDist = employees.map(e => ({
          employee: e,
          km: haversineKm(e.location.coordinates.latitude, e.location.coordinates.longitude, hq.latitude, hq.longitude),
        }));
        if (withDist.length === 0) return null;
        const sorted = [...withDist].sort((a, b) => a.km - b.km);
        const avgKm = withDist.reduce((s, d) => s + d.km, 0) / withDist.length;
        const closest = sorted[0];
        const furthest = sorted[sorted.length - 1];

        const buckets: [string, number][] = [
          ['Within 100 km', sorted.filter(d => d.km < 100).length],
          ['100–500 km', sorted.filter(d => d.km >= 100 && d.km < 500).length],
          ['500–2,000 km', sorted.filter(d => d.km >= 500 && d.km < 2000).length],
          ['2,000–5,000 km', sorted.filter(d => d.km >= 2000 && d.km < 5000).length],
          ['5,000+ km', sorted.filter(d => d.km >= 5000).length],
        ].filter(([, n]) => n > 0) as [string, number][];
        const maxBucket = Math.max(...buckets.map(([, n]) => n), 1);

        const useMiles = typeof navigator !== 'undefined' &&
          new Intl.NumberFormat(navigator.language, { style: 'unit', unit: 'kilometer' }).resolvedOptions().locale?.startsWith('en-US');
        void useMiles;

        return (
          <div className="card p-5">
            <SectionHeader
              icon={<Building2 className="h-4 w-4" />}
              title="Distance from HQ"
              subtitle={`${hq.name} · how far is each team member?`}
            />

            {/* Average stat */}
            <div className="mb-5">
              <StatPill
                label="Average distance from HQ"
                value={formatDistanceToHQ(avgKm, hq.name).split(' from ')[0]}
                color="text-primary-400"
              />
            </div>

            {/* Closest / Furthest */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              {[{ label: 'Closest to HQ', item: closest }, { label: 'Furthest from HQ', item: furthest }].map(({ label, item }) => (
                <div key={label} className="bg-bolt-bg border border-bolt-border rounded-lg p-4">
                  <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">{label}</p>
                  <Link to={`/employee/${item.employee.id}`} className="flex items-center gap-2.5 group">
                    <img
                      src={item.employee.avatar}
                      alt={item.employee.name}
                      className="w-8 h-8 rounded-full object-cover shrink-0 border border-bolt-border"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-bolt-text truncate group-hover:text-primary-400 transition-colors">{item.employee.name}</p>
                      <p className="text-xs text-bolt-textSubtle truncate">{item.employee.location.city}</p>
                    </div>
                  </Link>
                  <p className="text-xs font-mono text-bolt-textMuted mt-2">{formatDistanceToHQ(item.km, hq.name)}</p>
                </div>
              ))}
            </div>

            {/* Distance histogram */}
            <div className="space-y-2.5">
              {buckets.map(([label, count]) => (
                <HBar key={label} label={label} count={count} max={maxBucket} color="bg-primary-600" />
              ))}
            </div>
          </div>
        );
      })()}

      {/* Row 5: Geographic distribution */}
      {data.distanceStats && (
        <div className="card p-5">
          <SectionHeader
            icon={<Compass className="h-4 w-4" />}
            title="Geographic Distribution"
            subtitle="Distance-based team spread analysis"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
            {/* Avg distance */}
            <div className="bg-bolt-bg border border-bolt-border rounded-lg p-4">
              <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">Avg distance between members</p>
              <p className="text-2xl font-bold text-primary-400">
                {formatDistance(data.distanceStats.avgDistanceKm)}
              </p>
              <p className="text-xs text-bolt-textSubtle mt-1">across all member pairs</p>
            </div>

            {/* Closest on average */}
            <div className="bg-bolt-bg border border-bolt-border rounded-lg p-4">
              <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">Closest on average</p>
              <Link to={`/employee/${data.distanceStats.closestOnAverage.id}`} className="flex items-center gap-2.5 mt-1 group">
                <img
                  src={data.distanceStats.closestOnAverage.avatar}
                  alt={data.distanceStats.closestOnAverage.name}
                  className="w-8 h-8 rounded-full object-cover shrink-0 border border-bolt-border"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-bolt-text truncate group-hover:text-primary-400 transition-colors">{data.distanceStats.closestOnAverage.name}</p>
                  <p className="text-xs text-bolt-textSubtle truncate">{data.distanceStats.closestOnAverage.location.city}</p>
                </div>
              </Link>
              <p className="text-xs text-bolt-textSubtle mt-2">lowest avg distance to all others</p>
            </div>

            {/* Most central */}
            <div className="bg-bolt-bg border border-bolt-border rounded-lg p-4">
              <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">Most central</p>
              <Link to={`/employee/${data.distanceStats.mostCentral.id}`} className="flex items-center gap-2.5 mt-1 group">
                <img
                  src={data.distanceStats.mostCentral.avatar}
                  alt={data.distanceStats.mostCentral.name}
                  className="w-8 h-8 rounded-full object-cover shrink-0 border border-bolt-border"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-bolt-text truncate group-hover:text-primary-400 transition-colors">{data.distanceStats.mostCentral.name}</p>
                  <p className="text-xs text-bolt-textSubtle truncate">{data.distanceStats.mostCentral.location.city}</p>
                </div>
              </Link>
              <p className="text-xs text-bolt-textSubtle mt-2">closest to the team center</p>
            </div>

            {/* Most isolated */}
            <div className="bg-bolt-bg border border-bolt-border rounded-lg p-4">
              <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">Most isolated member</p>
              <Link to={`/employee/${data.distanceStats.mostIsolated.id}`} className="flex items-center gap-2.5 mt-1 group">
                <img
                  src={data.distanceStats.mostIsolated.avatar}
                  alt={data.distanceStats.mostIsolated.name}
                  className="w-8 h-8 rounded-full object-cover shrink-0 border border-bolt-border"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-bolt-text truncate group-hover:text-primary-400 transition-colors">{data.distanceStats.mostIsolated.name}</p>
                  <p className="text-xs text-bolt-textSubtle truncate">{data.distanceStats.mostIsolated.location.city}</p>
                </div>
              </Link>
              <p className="text-xs text-bolt-textSubtle mt-2">highest avg distance to all others</p>
            </div>

            {/* Largest cluster */}
            <div className="bg-bolt-bg border border-bolt-border rounded-lg p-4">
              <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">Largest cluster (within 1h flight)</p>
              <p className="text-2xl font-bold text-success-400 mb-1">
                {data.distanceStats.largestCluster.length}
                <span className="text-sm font-medium text-bolt-textMuted ml-1">members</span>
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                {data.distanceStats.largestCluster.slice(0, 6).map(e => (
                  <Link key={e.id} to={`/employee/${e.id}`}>
                    <img
                      src={e.avatar}
                      alt={e.name}
                      title={`${e.name} — ${e.location.city}`}
                      className="w-6 h-6 rounded-full object-cover border border-bolt-border hover:ring-2 hover:ring-primary-400 transition-all"
                    />
                  </Link>
                ))}
                {data.distanceStats.largestCluster.length > 6 && (
                  <div className="w-6 h-6 rounded-full bg-bolt-surface border border-bolt-border flex items-center justify-center">
                    <span className="text-[9px] font-semibold text-bolt-textMuted">+{data.distanceStats.largestCluster.length - 6}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-bolt-textSubtle mt-2">
                centered around {data.distanceStats.largestCluster[0]?.location.city}
              </p>
            </div>
          </div>

          {/* Cardinal extremes */}
          {employees.length > 0 && (() => {
            const northernmost = employees.reduce((a, b) => b.location.coordinates.latitude > a.location.coordinates.latitude ? b : a);
            const southernmost = employees.reduce((a, b) => b.location.coordinates.latitude < a.location.coordinates.latitude ? b : a);
            const easternmost  = employees.reduce((a, b) => b.location.coordinates.longitude > a.location.coordinates.longitude ? b : a);
            const westernmost  = employees.reduce((a, b) => b.location.coordinates.longitude < a.location.coordinates.longitude ? b : a);

            const CardinalCard = ({ dir, label, employee, coord, unit }: {
              dir: string; label: string; employee: typeof employees[0]; coord: number; unit: string;
            }) => (
              <div className="bg-bolt-bg border border-bolt-border rounded-lg p-4">
                <p className="text-[10px] font-medium text-bolt-textSubtle uppercase tracking-wide mb-2">
                  {dir} · Furthest {label}
                </p>
                <Link to={`/employee/${employee.id}`} className="flex items-center gap-2.5 mt-1 group">
                  <img
                    src={employee.avatar}
                    alt={employee.name}
                    className="w-8 h-8 rounded-full object-cover shrink-0 border border-bolt-border"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-bolt-text truncate group-hover:text-primary-400 transition-colors">{employee.name}</p>
                    <p className="text-xs text-bolt-textSubtle truncate">{[employee.location.city, employee.location.country].filter(Boolean).join(', ')}</p>
                  </div>
                </Link>
                <p className="text-xs text-bolt-textSubtle mt-2 font-mono">
                  {Math.abs(coord).toFixed(2)}° {unit}
                </p>
              </div>
            );

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-1 border-t border-bolt-border mt-1">
                <CardinalCard dir="↑" label="North" employee={northernmost} coord={northernmost.location.coordinates.latitude} unit="N" />
                <CardinalCard dir="↓" label="South" employee={southernmost} coord={southernmost.location.coordinates.latitude} unit="S" />
                <CardinalCard dir="→" label="East"  employee={easternmost}  coord={easternmost.location.coordinates.longitude}  unit="E" />
                <CardinalCard dir="←" label="West"  employee={westernmost}  coord={Math.abs(westernmost.location.coordinates.longitude)} unit="W" />
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default InsightsPage;
